import { Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import { getDefaultNominalDayHours, minutesToHours } from "PFCore/helpers/calendars";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { WeekDay } from "PFCore/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./info_icon.module.scss";

type InfoIconProps = {
  isTimeRule?: boolean;
};

const WEEKDAYS_ORDER: WeekDay[] = [
  WeekDay.Monday,
  WeekDay.Tuesday,
  WeekDay.Wednesday,
  WeekDay.Thursday,
  WeekDay.Friday,
  WeekDay.Saturday,
  WeekDay.Sunday
];

export const InfoIcon = ({ isTimeRule }: InfoIconProps) => {
  const { data: myAccountInfo } = useMyAccountInfo();
  const { t } = useTranslation("translation");

  const defaultNominalDayHours = getDefaultNominalDayHours(myAccountInfo);

  const tooltipContent = useMemo(() => {
    const nominalDays = myAccountInfo?.availability.default_calendar?.nominal_days ?? [];

    const defaultDays = WEEKDAYS_ORDER.map((dayName) => {
      const nominalDay = nominalDays.find((day) => day.day_name === dayName);
      if (nominalDay && nominalDay.working_minutes > 0) {
        return `${t(`weekdays.${dayName}`)}: ${minutesToHours(nominalDay.working_minutes, "h")}`;
      }
      return null;
    }).filter(Boolean);

    const firstLineText = isTimeRule
      ? defaultDays.join(", ")
      : t("defaultNominalDaysTooltip.title", { hours: defaultNominalDayHours });
    const secondLineText = isTimeRule
      ? t("defaultNominalDaysTooltip.timeRuleDescription")
      : t("defaultNominalDaysTooltip.description");

    return (
      <Stack>
        <Typography variant="bodyBold" tag="span">
          {firstLineText}
        </Typography>
        <Typography variant="bodyRegular" tag="span">
          {secondLineText}
        </Typography>
      </Stack>
    );
  }, [defaultNominalDayHours, isTimeRule, myAccountInfo?.availability.default_calendar?.nominal_days, t]);

  return (
    <Tooltip content={tooltipContent} triggerContainerProps={{ className: css.root }}>
      <Icon name="info" />
    </Tooltip>
  );
};
